import type { DocumentationContent } from "@/components/common/InfoPopover/InfoPopover.types";
import meanScoreColorGrading from "@/assets/images/mean-score-type-color-grading.png";

export const PAGE_DOCUMENTATION: Record<string, DocumentationContent> = {
  reputationQrq: {
    overview:
      "**Quest Reputation Quotient® (QuestRQ®)** is a proprietary reputation score developed by The Harris Poll. It represents a brand's overall reputation strength across six core dimensions, capturing how people judge the company behind the brand.\n\nQuestRQ® provides a single, standardized indicator of reputation performance. Higher scores signal a more resilient reputation and a stronger connection to customer likelihood.\n\nQuestRQ® is purpose-built for reputation. It operates as a distinct module with dedicated questions, a curated brand list, and a proprietary scoring model designed to measure company meaning, credibility, and relevance.",
    video: {
      src: "https://assets.theharrispoll.com/app/kb/videos/QB_04_QuestRQ_Video_v1.mp4",
      title: "QuestRQ - Walkthrough",
    },
    sections: [
      {
        title: "A Proprietary Score, Built on Decades of Science",
        content:
          "QuestRQ® is grounded in more than **30 years of research** from The Harris Poll's **Reputation Quotient® (RQ®)** framework, one of the most established and trusted measures of reputation.\n\nHarris QuestRQ modernizes that legacy for the speed and complexity of the modern world by:\n\n- **Preserving continuity** with long-standing RQ® benchmarks\n- **Embedding measurement** into an always-on digital platform\n- **Expanding the framework** to reflect cultural and societal dynamics in real time\n\nWhile the calculation is proprietary, the framework is designed to be **consistent, interpretable, and decision-ready** for leaders across communications, marketing, HR, and the C-suite.",
      },
      {
        title: "Core Reputation Metrics",
        content:
          "QuestRQ® is calculated across **six core dimensions**. Each reflects a distinct way consumers evaluate an organization:\n\n- **Trust**: \"Company I trust\"\n- **Vision**: \"Clear vision for the future\"\n- **Quality**: \"Offers high quality products and services\"\n- **Ethics**: \"Maintains high ethical standards\"\n- **Workplace Culture**: \"Good company to work for\"\n- **Relevance**: \"Shaping the trends and conversations happening today\"",
      },
      {
        title: "Mean Score Interpretation",
        content:
          "QuestRQ® metrics use a **Mean Score** methodology. Scores reflect the average level of agreement across respondents and are normalized for consistent comparison across brands and time.\n\n- **75+ (Resilient)**: Exceptional performance and high resilience\n- **65–74 (Strong)**: Strong performance with broad support\n- **55–64 (Average)**: Stable performance with limited differentiation\n- **45–54 (Weak)**: Emerging vulnerability or reputational friction\n- **Below 45 (Critical)**: Elevated reputational risk requiring attention",
        imageUrl: meanScoreColorGrading,
        imageCaption:
          "Color grading legend for Mean Score type metrics showing score ranges, performance labels, and associated colors",
      },
      {
        title: "Why Relevance Changes Everything",
        content:
          "Legacy reputation models were built for a slower world. They measured trust, ethics, and vision. While those pillars remain essential, they fail to measure **pulse**. Today, a company can be trusted and well run, and still disappear.\n\nQuestRQ® modernizes measurement by embedding **Relevance** directly into the framework. Relevance captures whether a company is shaping the trends of today or drifting to the margins of culture. It determines whether a reputation is **active or dormant**.\n\nIn dynamic markets, shifts in Relevance often precede broader reputation pressure. When a company begins to feel less culturally connected or less present in the conversation, other indicators may follow — depending on category, context, and competitive intensity.\n\nBy measuring relevance continuously, QuestRQ® surfaces **early signals** that legacy models and reconstituted brand scores cannot detect.",
      },
      {
        title: "Brand and Reputation, Working Together",
        content:
          "Brand equity and reputation answer different questions. **Brand equity explains choice**. It shows how strong a brand is as an option today. **Reputation explains context**. It shapes how that choice is interpreted and sustained over time.\n\n**QuestBrand** captures the commercial read: familiarity, consideration, quality, and momentum.\n\n**QuestRQ®** captures the meaning layer beneath it: trust, relevance, ethics, vision, and culture.\n\nViewed together, they allow leaders to understand whether brand performance is being **reinforced or constrained** by reputation.\n\n- **QuestBrand** shows where the brand stands.\n- **QuestRQ®** explains the forces acting on it.",
      },
    ],
  },
  visibility: {
    overview:
      "The size and quality of the demand pool you're playing in.\n\nBrand Visibility answers a deceptively simple question: How many people are actually reachable right now—and how meaningfully?",
    video: {
      src: "https://assets.theharrispoll.com/app/kb/videos/QB_02_BrandVisibility_Video_v1.mp4",
      title: "Brand Visibility - Walkthrough",
      // thumbnailUrl: "https://your-cdn/path/thumb-visibility.jpg",
    },
    sections: [
      {
        title: "Three Signals That Shape Visibility",
        content:
          "Brand Visibility captures three distinct signals that behave very differently:\n\n- **Awareness** establishes reach. It measures how many people recognize your brand.\n- **Familiarity** signals depth. It shows how well people know your brand beyond simple recognition.\n- **Ad Recall** reflects immediate campaign impact. It indicates whether recent marketing efforts are breaking through.\n\nIndividually, each tells a partial story. Together, they reveal whether marketing is expanding the top of the funnel in a way that can actually support growth.",
      },
      {
        title: "When Visibility Compounds—and When It Doesn't",
        content:
          "Not all visibility is created equal. The relationship between these three signals determines whether visibility is compounding or creating noise:\n\n- **Awareness without Familiarity** creates noise. People recognize your brand but don't know enough to meaningfully engage.\n- **Ad Recall without sustained Awareness** fades fast. Campaigns may spike recall temporarily, but without ongoing awareness, the impact dissipates quickly.\n- **When all three move together**, demand isn't just growing—it's compounding. Awareness creates reach, Familiarity adds depth, and Ad Recall reflects immediate impact, creating a foundation for sustainable growth.\n\nBrand Visibility sets the ceiling for everything that follows. You can't convert what you haven't truly reached.",
      },
    ],
  },
  equity: {
    overview:
      "The hardest brand metric to move—and the one that matters most.\n\nEquity is the only composite metric in the Brand section. It reflects the combined strength of four forces: Familiarity, Quality, Consideration, and Momentum.",
    video: {
      src: "https://assets.theharrispoll.com/app/kb/videos/QB_03_Equity_Video_v1.mp4",
      title: "Equity - Walkthrough",
    },
    sections: [
      {
        title: "Why Equity moves differently",
        content:
          "Because it's a straight average, Equity doesn't respond to one-off spikes or isolated wins. Movement requires multiple perceptions to shift at once. That's precisely why changes here are meaningful.\n\nEach component plays a distinct role. **Familiarity** brings scale. **Quality** anchors trust. **Consideration** reflects choice. **Momentum** captures direction. Equity is where those forces intersect.",
      },
      {
        title: "What makes Equity uniquely valuable",
        content:
          "- **It resists volatility**\n- **It rewards consistency**\n- **It distinguishes real brand strength from short-term attention**\n\nWhen Equity moves, it's not because something happened.\n\nIt's because perception has changed.",
      },
    ],
  },
  emotional: {
    overview:
      "How your brand is framed—and why it wins or loses in moments of choice.\n\nEmotional Imagery reveals the associations consumers attach to brands: innovative, premium, trustworthy, fun, bold, dependable, and more.\n\nThese attributes don't replace functional metrics. They explain them.\n\nTwo brands can look identical on Awareness or Consideration and still perform very differently. Imagery shows why—by revealing how brands occupy emotional space relative to competitors.",
    video: {
      src: "https://assets.theharrispoll.com/app/kb/videos/QB_06_EmotionalImagery_Video_v1.mp4",
      title: "Emotional Imagery - Walkthrough",
    },
    sections: [
      {
        title: "Making positioning visible",
        content:
          "This module powers **Perceptual Maps**, where brands cluster around the attributes they over-index on relative to their peers, and **Differentiation Maps**, which show how a brand compares to the average of its competitive set.\n\nDifferentiation visualizes where a brand under-indexes or over-indexes on specific attributes, and whether those attributes are central or peripheral to how the brand is known.\n\nImagery is often the missing layer between what consumers say they'll do and what they actually choose.",
      },
    ],
  },
  conversion: {
    overview:
      "Where demand turns into behavior—and where it quietly leaks.\n\nThe Conversion Funnel tracks how consumers move from Awareness to Trial, Usage, and Recommendation.",
    video: {
      src: "https://assets.theharrispoll.com/app/kb/videos/QB_05_Conversion_Video_v1.mp4",
      title: "Conversion Funnel - Walkthrough",
    },
    sections: [
      {
        title: "More than levels—conversion clarity",
        content:
          "Beyond stage sizes, the funnel highlights **conversion rates** between each step, making friction explicit. These percentages reveal whether growth challenges stem from scale, efficiency, or experience.\n\nStrong lower-funnel conversion with modest growth often points to reach or availability constraints. Sharp drop-offs at specific stages suggest messaging, pricing, distribution, or product issues.",
      },
      {
        title: "Why Recommendation matters",
        content:
          "Recommendation reflects more than satisfaction. It signals **advocacy**—the moment where experience reinforces demand and fuels future growth.\n\nThe funnel doesn't guess at causes.\n\nIt tells you exactly where to look.",
      },
    ],
  },
  exclusivequestions: {
    overview:
      "Proprietary measurement, natively integrated.\n\nExclusive Questions provide a dedicated module for brand-specific questions that are visible only to your organization.",
    video: {
      src: "https://assets.theharrispoll.com/app/kb/videos/QB_07_ExclusiveQuestions_Video_v1.mp4",
      title: "Exclusive Questions - Walkthrough",
    },
    sections: [
      {
        title: "Built into the system, not bolted on",
        content:
          "Your proprietary questions live inside **QuestBrand's** core analytical framework, with the same segmentation, trending, and filtering capabilities as syndicated metrics—without parallel workflows, exports, or disconnected outputs.",
      },
      {
        title: "Designed for clarity and control",
        content:
          "Results are analyzed in a purpose-built environment that makes custom data as usable and flexible as the rest of the platform. Nothing feels secondary. Nothing feels improvised.",
      },
      {
        title: "Designed to scale cleanly",
        content:
          "Behind the scenes, QuestBrand automatically manages interview length and sample allocation as Exclusive Questions expand, preserving data quality without manual tradeoffs.",
      },
      {
        title: "Available the moment it's collected",
        content:
          "Once data hits the platform, it is immediately ready for analysis.\n\n- No processing queues.\n- No vendor delays.\n- No handoffs to external tools.\n\nExclusive Questions turn proprietary inputs into a persistent analytical layer—one that evolves with the business rather than expiring after a single readout.",
      },
    ],
  },
  reports: {
    overview:
      "Automated reporting, built into the platform.\n\nReport Builder converts any saved view across **QuestBrand** and **QuestRQ** into a live report that stays connected to the underlying data.",
    video: {
      src: "https://assets.theharrispoll.com/app/kb/videos/QB_08_ReportBuilder_Video_v1.mp4",
      title: "Report Builder - Walkthrough",
    },
    sections: [
      {
        title: "Create once. Update automatically",
        content:
          "Reports are not rebuilt every time they're shared. They are created once and refresh automatically as new data arrives, preserving structure while the numbers evolve.",
      },
      {
        title: "Built for automation at scale",
        content:
          "Slides can be saved, reordered, cloned, and reused. Date ranges can be edited in bulk. Entire reports can be refreshed without recreating a single chart.",
      },
      {
        title: "Consistency by design",
        content:
          "Every chart and report is generated from the same saved logic—meaning the same metrics, filters, and definitions wherever they appear. When data updates, it updates everywhere.",
      },
      {
        title: "From analysis to infrastructure",
        content:
          "Reports can be reviewed live in-platform, shared across teams and leadership, or exported when needed. The system holds, even as questions change.\n\nReport Builder is how analysis becomes durable, repeatable, and operational—so teams spend less time rebuilding outputs and more time acting on them.",
      },
    ],
  },
};

/**
 * Get documentation content for a given route key
 * @param routeKey - The route key (e.g., 'reputationQrq', 'visibility', 'equity', 'emotional', 'conversion', 'exclusivequestions', 'reports', etc.)
 * @returns DocumentationContent or null if not found
 */
export const getPageDocumentation = (
  routeKey: string | undefined
): DocumentationContent | null => {
  if (!routeKey) return null;
  return PAGE_DOCUMENTATION[routeKey] || null;
};
